import type { DeviceLengths, HeaderLook } from "@/types/look-system";
import { isLookSystem } from "@/utils/look-gate";

export type HeaderTop = NonNullable<HeaderLook["top"]>;
export type HeaderShift = NonNullable<HeaderLook["shift"]>;
export type HeaderAfter = NonNullable<HeaderLook["after"]>;
export type HeaderLengths = Required<DeviceLengths>;

/** `styleSystem.header` with the page's own choices and every default applied (spec §9.4). */
export interface HeaderSettings {
  top: HeaderTop;
  trigger: { kind: "section" | "px"; px: number };
  shift: HeaderShift;
  /** An absent part keeps the header configuration's own height; `after: 'same'` is »Ingen ændring«. */
  height: { top?: HeaderLengths; after?: HeaderLengths | "same" };
  after: HeaderAfter;
  /** Toner's duration in ms. */
  duration: number;
}

export interface HeaderInput {
  y: number;
  /** The scroll position the hide direction is measured from: the previous frame's `lastY`, or `y` on the first. */
  lastY: number;
  triggerY: number;
  settings: HeaderSettings;
  reducedMotion: boolean;
  /** The previous frame's `hidden`; kept while the movement stays within the tolerance. */
  hidden?: boolean;
}

export interface HeaderFrame {
  /** The surface: `top` is the see-through look above the trigger, which Fast never shows. */
  state: "top" | "switched";
  /** Past the trigger; the height follows it, also for Fast. */
  scrolled: boolean;
  hidden: boolean;
  compact: boolean;
  /** 0–1: Følger scroll's fill, otherwise 0 or 1. */
  progress: number;
  duration: number;
  lastY: number;
}

export const HEADER_HIDE_TOLERANCE = 4;
export const HEADER_DURATION = 300;
export const FIRST_SECTION = "[data-header-content] [data-section-type]";
const REDUCED_MOTION = "(prefers-reduced-motion: reduce)";
const TOPS: readonly HeaderTop[] = ["solid", "clear", "semi", "frost"];
const SHIFTS: readonly HeaderShift[] = ["instant", "fade", "scroll"];
const AFTERS: readonly HeaderAfter[] = ["stay", "hide", "compact"];
const PX = /^\d+(\.\d+)?px$/;

type Loose = Record<string, unknown>;
const record = (value: unknown): Loose =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Loose)
    : {};
const oneOf = <T extends string>(values: readonly T[], value: unknown) =>
  values.find((option) => option === value);
const numberIn = (value: unknown, min: number, max: number) =>
  typeof value === "number" &&
  Number.isFinite(value) &&
  value >= min &&
  value <= max
    ? value
    : undefined;

function lengths(value: unknown): HeaderLengths | undefined {
  const { desktop, mobile } = record(value);
  const valid = (length: unknown) =>
    typeof length === "string" && PX.test(length) ? length : undefined;
  const wide = valid(desktop);
  const narrow = valid(mobile) ?? wide;
  return wide && narrow ? { desktop: wide, mobile: narrow } : undefined;
}

function trigger(value: unknown): HeaderSettings["trigger"] | undefined {
  if (value === "section") return { kind: "section", px: 1 };
  const { kind, px } = record(value);
  if (kind === "section") return { kind, px: 1 };
  if (kind === "px") return { kind, px: numberIn(px, 0, 100000) ?? 1 };
  return undefined;
}

/** The look a header configuration already has above the trigger: see-through only where it is drawn over the page. */
function configuredTop(header: Loose): HeaderTop {
  if (header.type === "TransparentHeader")
    return header.isVisible ? "solid" : "clear";
  if (header.type === "ClassicHeader") {
    const classic = record(header.classic);
    return (header.contentPlacement === "overlay" ||
      classic.mobileOverlay === true) &&
      !classic.solid
      ? "clear"
      : "solid";
  }
  return "solid";
}

/** What a header configuration already does on scroll: Standard slides away, Klassisk unless told not to. */
function configuredAfter(header: Loose): HeaderAfter {
  const type = header.type ?? "DefaultHeader";
  if (type === "DefaultHeader") return "hide";
  if (type === "ClassicHeader")
    return record(header.classic).hideOnScroll === false ? "stay" : "hide";
  return "stay";
}

/**
 * The header settings a page shows: the page's `layout.header.look` over the website's `styleSystem.header`. A page
 * that is solid from the top in today's keys (`isVisible`, a solid presentation) is Fast unless its own look says
 * otherwise. Without a stored `top` or `after`, the header configuration decides, so a solid header is Fast and Standard
 * still slides away. Other defaults: switch at the end of the first section, Toner over 300 ms, heights from the header
 * configuration.
 */
export function resolveHeaderSettings(
  website: unknown,
  pageHeader: unknown,
  header: unknown,
): HeaderSettings {
  const site = record(website);
  const pageConfig = record(pageHeader);
  const page = record(pageConfig.look);
  const config = record(header);
  const pageSolid =
    pageConfig.isVisible === true || config.presentationMode === "solid";
  const height = { ...record(site.height), ...record(page.height) };
  const after = height.after === "same" ? "same" : lengths(height.after);
  const top = lengths(height.top);
  return {
    top:
      oneOf(TOPS, page.top) ??
      (pageSolid ? "solid" : undefined) ??
      oneOf(TOPS, site.top) ??
      configuredTop(config),
    trigger: trigger(page.trigger) ??
      trigger(site.trigger) ?? { kind: "section", px: 1 },
    shift: oneOf(SHIFTS, page.shift) ?? oneOf(SHIFTS, site.shift) ?? "fade",
    height: { ...(top ? { top } : {}), ...(after ? { after } : {}) },
    after:
      oneOf(AFTERS, page.after) ??
      oneOf(AFTERS, site.after) ??
      configuredAfter(config),
    duration:
      numberIn(page.duration, 0, 5000) ??
      numberIn(site.duration, 0, 5000) ??
      HEADER_DURATION,
  };
}

/** resolveHeaderSettings behind the gate: undefined for a website not on the look system. */
export function lookHeaderSettings(
  theme: unknown,
  pageHeader: unknown,
  header: unknown,
): HeaderSettings | undefined {
  return isLookSystem(theme)
    ? resolveHeaderSettings(theme.styleSystem?.header, pageHeader, header)
    : undefined;
}

/**
 * The scroll position past which the header switches. A section trigger is the end of the page's first section less
 * the header's height, so the header turns as its lower edge leaves the section. Fast has no switch of its own and
 * changes height after its pixel trigger, or at once.
 */
export function headerTriggerY(
  settings: HeaderSettings,
  measured: { sectionEnd?: number; headerHeight?: number } = {},
): number {
  if (settings.trigger.kind === "px") return settings.trigger.px;
  if (settings.top === "solid" || measured.sectionEnd === undefined) return 1;
  return Math.max(1, measured.sectionEnd - (measured.headerHeight ?? 0));
}

export function nextHeaderState({
  y,
  lastY,
  triggerY,
  settings,
  reducedMotion,
  hidden = false,
}: HeaderInput): HeaderFrame {
  const position = Math.max(0, y);
  const scrolled = position > triggerY;
  const moved = position - lastY;
  const decided = Math.abs(moved) > HEADER_HIDE_TOLERANCE;
  const hides = settings.after === "hide" && !reducedMotion && scrolled;
  const follows = settings.shift === "scroll" && !reducedMotion;
  return {
    state: settings.top === "solid" || scrolled ? "switched" : "top",
    scrolled,
    hidden: hides && (decided ? moved > 0 : hidden),
    compact: settings.after === "compact" && scrolled,
    progress:
      settings.top === "solid"
        ? 1
        : follows && triggerY > 0
          ? Math.min(1, position / triggerY)
          : Number(scrolled),
    duration: reducedMotion ? 0 : settings.duration,
    lastY: decided ? position : lastY,
  };
}

/** The frame the server renders: the top of the page, before anything is measured. */
export function initialHeaderFrame(settings: HeaderSettings): HeaderFrame {
  return nextHeaderState({
    y: 0,
    lastY: 0,
    triggerY: headerTriggerY(settings),
    settings,
    reducedMotion: false,
  });
}

/** What header-look CSS reads on the controller's wrapper: `data-header-*` attributes and `--re-header-*` values. */
export function headerStateAttributes(
  settings: HeaderSettings,
  frame: HeaderFrame,
): Record<string, string> {
  return {
    "data-header-state": frame.state,
    "data-header-top": settings.top,
    "data-header-shift": settings.shift,
    "data-header-after": settings.after,
    ...(frame.scrolled ? { "data-header-scrolled": "" } : {}),
    ...(frame.hidden ? { "data-header-hidden": "" } : {}),
    ...(frame.compact ? { "data-header-compact": "" } : {}),
  };
}

export function headerStateVars(
  settings: HeaderSettings,
  frame: HeaderFrame,
): Record<string, string> {
  const { top, after } = settings.height;
  const afterLengths = after === "same" ? top : after;
  return {
    "--re-header-progress": String(Math.round(frame.progress * 1000) / 1000),
    "--re-header-duration": `${frame.duration}ms`,
    ...(top
      ? {
          "--re-header-height-top-desktop": top.desktop,
          "--re-header-height-top-mobile": top.mobile,
        }
      : {}),
    ...(afterLengths
      ? {
          "--re-header-height-after-desktop": afterLengths.desktop,
          "--re-header-height-after-mobile": afterLengths.mobile,
        }
      : {}),
  };
}

const READY = "data-header-ready";
const FLAGS = [
  "data-header-scrolled",
  "data-header-hidden",
  "data-header-compact",
];
const HEIGHTS = ["top", "after"].flatMap((part) =>
  ["desktop", "mobile"].map((device) => `--re-header-height-${part}-${device}`),
);

/** Dispatched on the controller's wrapper as the controller takes the header over from headerFirstFrameScript. */
export const HEADER_TAKEOVER = "re-header-takeover";

/**
 * The inline script right after the controller's wrapper, for the time before hydration. It writes the first frame for
 * the current scroll position (nextHeaderState with `lastY = y`, so never hidden) on the wrapper and on the header's
 * `data-isvisible`: at once, in every animation frame until the page has loaded (reading the scroll position there
 * makes the browser restore a reload's position before it paints) and on scroll. On HEADER_TAKEOVER it stops and puts
 * back what the server rendered, which is what hydration found, so the controller starts from the same DOM as without
 * it. A header part whose look follows the frame other than through data-isvisible is drawn from the wrapper in
 * header-look.css, or it shows the server's state until hydration.
 */
export function headerFirstFrameScript(settings: HeaderSettings): string {
  const input = JSON.stringify({
    top: settings.top,
    kind: settings.trigger.kind,
    px: settings.trigger.px,
    shift: settings.shift,
    after: settings.after,
    duration: settings.duration,
  }).replace(/</g, "\\u003c");
  const text = JSON.stringify;
  return `(()=>{try{const s=${input},w=window,d=document,root=d.currentScript.previousElementSibling,reduced=w.matchMedia(${text(REDUCED_MOTION)}),inner=Array.from(root.querySelectorAll("[data-isvisible]"),(el)=>[el,el.getAttribute("data-isvisible")]),saved=[${[...FLAGS, "data-header-state", "style"].map((name) => text(name)).join(",")}].map((name)=>[name,root.getAttribute(name)]);let height=0,scrolled=false,frame=0;const flag=(name,on)=>on?root.setAttribute(name,""):root.removeAttribute(name),schedule=()=>{if(!frame)frame=w.requestAnimationFrame(write)},write=()=>{frame=0;const y=Math.max(0,w.scrollY);if(!scrolled){const header=root.querySelector("header");if(header)height=header.getBoundingClientRect().height}let trigger=s.px;if(s.kind==="section"){const section=s.top==="solid"?null:d.querySelector(${text(FIRST_SECTION)});trigger=section?Math.max(1,section.getBoundingClientRect().bottom+y-height):1}scrolled=y>trigger;const progress=s.top==="solid"?1:s.shift==="scroll"&&!reduced.matches&&trigger>0?Math.min(1,y/trigger):Number(scrolled);root.setAttribute("data-header-state",s.top==="solid"||scrolled?"switched":"top");flag("data-header-scrolled",scrolled);flag("data-header-compact",s.after==="compact"&&scrolled);root.style.setProperty("--re-header-progress",String(Math.round(progress*1000)/1000));root.style.setProperty("--re-header-duration",(reduced.matches?0:s.duration)+"ms");for(const [el,value] of inner)el.setAttribute("data-isvisible",scrolled?"true":value);if(d.readyState!=="complete")schedule()};write();w.addEventListener("scroll",schedule,{passive:true});root.addEventListener(${text(HEADER_TAKEOVER)},()=>{w.cancelAnimationFrame(frame);w.removeEventListener("scroll",schedule);for(const [name,value] of saved)value===null?root.removeAttribute(name):root.setAttribute(name,value);for(const [el,value] of inner)el.setAttribute("data-isvisible",value)},{once:true})}catch(error){}})()`;
}

function writeHeaderState(
  root: HTMLElement,
  settings: HeaderSettings,
  frame: HeaderFrame,
) {
  const attributes = headerStateAttributes(settings, frame);
  for (const flag of FLAGS)
    if (!(flag in attributes)) root.removeAttribute(flag);
  for (const [name, value] of Object.entries(attributes))
    root.setAttribute(name, value);
  const vars = headerStateVars(settings, frame);
  for (const name of HEIGHTS)
    if (!(name in vars)) root.style.removeProperty(name);
  for (const [name, value] of Object.entries(vars))
    root.style.setProperty(name, value);
}

const sameState = (a: HeaderFrame, b: HeaderFrame) =>
  a.state === b.state &&
  a.scrolled === b.scrolled &&
  a.hidden === b.hidden &&
  a.compact === b.compact &&
  a.duration === b.duration;

/**
 * Keeps `root`'s header state in step with the scroll position, once per animation frame, and returns the cleanup.
 * The first frame is computed and written at once, so a call before paint shows the right state at a restored scroll
 * position. `onFrame` runs only when the state changes, not for each step of Følger scroll's progress, which is written
 * to `root` directly. Focus inside a hidden header shows it. `data-header-ready` follows two frames later, once the
 * browser has drawn the first state, so transitions keyed on it never animate the correction from the server's frame.
 */
export function watchHeaderState(
  win: Window & typeof globalThis,
  root: HTMLElement,
  settings: HeaderSettings,
  onFrame: (frame: HeaderFrame) => void,
): () => void {
  const reduced = win.matchMedia(REDUCED_MOTION);
  let shown: HeaderFrame | undefined;
  let lastY = Math.max(0, win.scrollY);
  let hidden = false;
  let headerHeight = 0;
  let pending = 0;
  let ready = 0;

  const measureHeader = () => {
    if (!shown?.scrolled)
      headerHeight =
        root.querySelector("header")?.getBoundingClientRect().height ??
        headerHeight;
  };
  const sectionEnd = (y: number) => {
    if (settings.trigger.kind !== "section" || settings.top === "solid")
      return undefined;
    const section = win.document.querySelector(FIRST_SECTION);
    return section ? section.getBoundingClientRect().bottom + y : undefined;
  };
  const update = () => {
    pending = 0;
    const y = Math.max(0, win.scrollY);
    const frame = nextHeaderState({
      y,
      lastY,
      triggerY: headerTriggerY(settings, {
        sectionEnd: sectionEnd(y),
        headerHeight,
      }),
      settings,
      reducedMotion: reduced.matches,
      hidden,
    });
    lastY = frame.lastY;
    hidden = frame.hidden;
    if (shown && frame.progress === shown.progress && sameState(shown, frame))
      return;
    writeHeaderState(root, settings, frame);
    if (!shown || !sameState(shown, frame)) onFrame(frame);
    shown = frame;
  };
  const schedule = () => {
    if (!pending) pending = win.requestAnimationFrame(update);
  };
  const resize = () => {
    measureHeader();
    schedule();
  };
  const reveal = () => {
    if (!hidden) return;
    hidden = false;
    lastY = Math.max(0, win.scrollY);
    update();
  };

  measureHeader();
  update();
  ready = win.requestAnimationFrame(() => {
    ready = win.requestAnimationFrame(() => {
      ready = 0;
      root.setAttribute(READY, "");
    });
  });
  win.addEventListener("scroll", schedule, { passive: true });
  win.addEventListener("resize", resize);
  win.addEventListener("load", schedule);
  win.addEventListener("pageshow", schedule);
  reduced.addEventListener?.("change", schedule);
  root.addEventListener("focusin", reveal);
  return () => {
    if (pending) win.cancelAnimationFrame(pending);
    if (ready) win.cancelAnimationFrame(ready);
    pending = ready = 0;
    root.removeAttribute(READY);
    win.removeEventListener("scroll", schedule);
    win.removeEventListener("resize", resize);
    win.removeEventListener("load", schedule);
    win.removeEventListener("pageshow", schedule);
    reduced.removeEventListener?.("change", schedule);
    root.removeEventListener("focusin", reveal);
  };
}

/** Calls `onDown` each time the page scrolls down by 2 px or more, measured from the last position that moved as far. */
export function watchScrollDown(
  win: Window & typeof globalThis,
  onDown: () => void,
): () => void {
  let previous = Math.max(0, win.scrollY);
  const onScroll = () => {
    const current = Math.max(0, win.scrollY);
    if (Math.abs(current - previous) < 2) return;
    if (current > previous) onDown();
    previous = current;
  };
  win.addEventListener("scroll", onScroll, { passive: true });
  return () => win.removeEventListener("scroll", onScroll);
}
